import { useCallback, useEffect, useRef, useState } from 'react';
import { useAccount, useChainId, useSignMessage } from 'wagmi';
import { createSiweMessage } from 'viem/siwe';
import { getNonce, verifySiwe, getSession, signOut as apiSignOut } from '../api/client';

export type SiweStatus = 'idle' | 'authenticating' | 'authenticated' | 'error';

// Manages a Sign-In With Ethereum session against the backend so the
// authenticated wallet can be used as a durable identity for server-side
// persistence (achievements, etc.). The wallet connection itself is handled by
// wagmi/useCoinbaseWallet — this layer adds the cryptographic proof of control.
export function useSiweAuth() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { signMessageAsync } = useSignMessage();

  const [authedAddress, setAuthedAddress] = useState<string | null>(null);
  const [status, setStatus] = useState<SiweStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // On mount, adopt any existing server session (cookie survives reloads).
  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((s) => {
        if (!cancelled && s.authenticated && s.address) {
          setAuthedAddress(s.address);
          setStatus('authenticated');
        }
      })
      .catch(() => {
        /* not signed in — stay idle */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    if (!address) return;
    setError(null);
    setStatus('authenticating');
    try {
      const { nonce } = await getNonce();
      const message = createSiweMessage({
        address,
        chainId,
        domain: window.location.host,
        nonce,
        uri: window.location.origin,
        version: '1',
        statement: 'Sign in to sync your Histocracy achievements across devices.',
      });
      const signature = await signMessageAsync({ message });
      const result = await verifySiwe(message, signature);
      setAuthedAddress(result.address);
      setStatus('authenticated');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign-in failed';
      setError(msg);
      setStatus('error');
    }
  }, [address, chainId, signMessageAsync]);

  const signOut = useCallback(async () => {
    try {
      await apiSignOut();
    } catch {
      // best-effort — clear local state regardless
    }
    setAuthedAddress(null);
    setStatus('idle');
    setError(null);
  }, []);

  // If the authenticated wallet changes or disconnects, the session no longer
  // matches the active account — drop it so we don't sync to the wrong wallet.
  const prevAddress = useRef<string | undefined>(address);
  useEffect(() => {
    if (!isConnected) {
      if (authedAddress) {
        setAuthedAddress(null);
        setStatus('idle');
      }
    } else if (
      authedAddress &&
      address &&
      address.toLowerCase() !== authedAddress.toLowerCase()
    ) {
      // Switched accounts in the wallet — require a fresh sign-in.
      setAuthedAddress(null);
      setStatus('idle');
    }
    prevAddress.current = address;
  }, [isConnected, address, authedAddress]);

  return {
    authedAddress,
    isAuthed: status === 'authenticated' && !!authedAddress,
    canSignIn: isConnected && !!address && status !== 'authenticated',
    status,
    error,
    signIn,
    signOut,
  };
}
